import { Banner } from "components";
import React from "react";
import { useTranslation } from "react-i18next";

import "./PrivacyPolicy.styles.scss";

const DropshotPrivacyPolicy = () => {
	const { t } = useTranslation(["common"]);

	return (
		<div>
			<Banner title={t("common:menus.privacyPolicy")} />

			<div className="privacy-policy-wrapper">
				<div className="privacy-policy-content">
					<p className="effective-date">Last Updated: March 2026</p>

					<p className="intro">
						This Privacy Policy explains how <strong>Drop Shot 3D</strong> (&quot;the Game&quot;) handles
						information. The Game is published by <strong>Eurycele Private Limited</strong> (&quot;we,&quot;
						&quot;our,&quot; or &quot;us&quot;).
					</p>
					<p className="intro">
						We respect user privacy and aim to provide a safe experience for all players. Drop Shot 3D is designed
						to work without account creation, user-to-user communication, or direct submission of personal profile
						information.
					</p>

					<section className="policy-section">
						<h2>1. Scope</h2>
						<p>
							This Privacy Policy applies to the mobile game Drop Shot 3D and describes how information is
							handled when users download, install, and play the Game.
						</p>
					</section>

					<section className="policy-section">
						<h2>2. Target Age Group</h2>
						<p>
							Drop Shot 3D is intended for a general audience aged <strong>16 years and above (16+)</strong>.
						</p>
						<p>
							The Game does not allow users to create an in-app account and does not require users to provide
							profile details such as name, username, address, or phone number in order to play.
						</p>
						<p>
							The Game is not designed for children. If you are a parent or guardian and believe a child has used
							the Game in a way that raises a privacy concern, please contact us.
						</p>
					</section>

					<section className="policy-section">
						<h2>3. Information We Collect</h2>
						<p>
							We do <strong>not</strong> ask players to create an account or directly submit personal profile
							information such as full name, postal address, phone number, contacts, messages, photos, or payment
							card details in order to use the Game.
						</p>
						<p>
							However, the Game uses third-party advertising and mediation services, including
							<strong> Unity LevelPlay / ironSource</strong>. As part of ad delivery, measurement, and fraud
							prevention, these services may automatically collect or process certain categories of data, such
							as:
						</p>
						<ul>
							<li>Approximate location</li>
							<li>App activity related to ad interactions and other actions</li>
							<li>Diagnostics and app performance information</li>
							<li>Device or other identifiers, including advertising-related identifiers where available</li>
						</ul>
						<p>
							These categories are processed to support advertising, analytics, and fraud prevention, consistent
							with the Game&apos;s Google Play Data safety declarations and the relevant SDK provider
							documentation.
						</p>
					</section>

					<section className="policy-section">
						<h2>4. Information Stored on the Device</h2>
						<p>
							The Game may store limited gameplay-related information{" "}
							<strong>locally on the user&apos;s device</strong>, such as:
						</p>
						<ul>
							<li>Level progress</li>
							<li>Unlocked content</li>
							<li>Game settings</li>
							<li>Local gameplay preferences</li>
						</ul>
						<p>
							This gameplay data is intended for normal operation of the Game and may remain on the user&apos;s
							device rather than being stored by us on our own servers.
						</p>
					</section>

					<section className="policy-section">
						<h2>5. Data Sharing</h2>
						<p>
							We do not share user data <strong>with other users</strong>. The Game does not include public
							profiles, friend systems, user-to-user chat, direct messaging, or user-generated content sharing.
						</p>
						<p>
							We may, however, allow certain data categories described above to be shared with or accessed by
							third-party advertising, mediation, measurement, and fraud-prevention partners that support ad
							monetization in the Game.
						</p>
						<p>
							We do not sell player names, email addresses, or in-app account data because the Game does not
							collect such account data from players.
						</p>
					</section>

					<section className="policy-section">
						<h2>6. Third-Party Services</h2>
						<p>
							Drop Shot 3D may use third-party services and SDKs, including advertising and mediation providers.
						</p>
						<p>These providers may process limited data categories for purposes such as:</p>
						<ul>
							<li>Advertising or marketing</li>
							<li>Analytics and measurement</li>
							<li>Fraud prevention, security, and compliance</li>
						</ul>
						<p>
							Please note that third-party providers may have their own privacy notices and handling practices.
							This Privacy Policy applies to Drop Shot 3D as operated by Eurycele Private Limited and should be
							read together with any applicable third-party privacy documentation.
						</p>
					</section>

					<section className="policy-section">
						<h2>7. Children&apos;s Privacy</h2>
						<p>We take children&apos;s privacy seriously.</p>
						<p>
							Drop Shot 3D is intended for users aged <strong>16+</strong> and is not designed for children.
						</p>
						<p>
							In the United States, the Children&apos;s Online Privacy Protection Act (COPPA) applies to online
							services directed to children under 13, or services with actual knowledge that they are collecting
							personal information from children under 13.
						</p>
						<p>
							For users in the European Union and the United Kingdom, additional protections may apply to
							children&apos;s personal data under GDPR / UK GDPR principles and related child-data safeguards.
						</p>
						<p>
							If a parent or guardian believes that a child has used the Game in a way that raises a privacy
							concern, they may contact us and we will review the matter and take appropriate action.
						</p>
					</section>

					<section className="policy-section">
						<h2>8. Legal and Regulatory Compliance</h2>
						<p>
							We aim to describe our data practices transparently and to align, where applicable, with relevant
							privacy and platform requirements, including:
						</p>
						<ul>
							<li>
								<strong>Google Play Data safety and privacy policy requirements</strong>, which require
								developers to accurately disclose app and SDK data collection, use, and sharing practices.
							</li>
							<li>
								<strong>COPPA</strong> in the United States for child-directed services and collection of
								personal information from children under 13.
							</li>
							<li>
								<strong>GDPR / UK GDPR</strong> principles and child-data safeguards where applicable.
							</li>
							<li>
								<strong>India&apos;s Digital Personal Data Protection Act, 2023</strong>, where applicable to
								digital personal data processing.
							</li>
						</ul>
					</section>

					<section className="policy-section">
						<h2>9. Security</h2>
						<p>
							We aim to use reasonable administrative, technical, and organizational measures appropriate to the
							Game and the services integrated with it.
						</p>
						<p>
							According to the relevant SDK provider documentation used for Google Play Data safety declarations,
							applicable SDK data is encrypted in transit.
						</p>
					</section>

					<section className="policy-section">
						<h2>10. User Rights and Data Deletion Requests</h2>
						<p>
							The Game does not provide user account creation. However, if you have a privacy question or would
							like to submit a data-related request, you may contact us at{" "}
							<a className="contact-link" href="mailto:connect@eurycele.com">
								connect@eurycele.com
							</a>
							.
						</p>
						<p>
							Where a request relates to data processed by third-party advertising or mediation partners, we may
							direct the request to the relevant provider or guide you to the appropriate privacy request
							mechanism.
						</p>
					</section>

					<section className="policy-section">
						<h2>11. International Users</h2>
						<p>
							Drop Shot 3D may be available in multiple countries. Third-party service providers used by the Game
							may process data across different jurisdictions in accordance with their legal and contractual
							requirements.
						</p>
					</section>

					<section className="policy-section">
						<h2>12. Changes to This Privacy Policy</h2>
						<p>
							We may update this Privacy Policy from time to time to reflect changes in the Game, platform
							requirements, legal obligations, or third-party services used by the Game.
						</p>
						<p>
							When we update this policy, we will revise the Last Updated date above. Continued use of the Game
							after an updated policy becomes effective means the updated version applies.
						</p>
					</section>

					<section className="policy-section">
						<h2>13. Contact Us</h2>
						<p>If you have questions about this Privacy Policy or Drop Shot 3D, you may contact us at:</p>
						<address>
							<strong>Eurycele Private Limited</strong>
							<br />
							Email:{" "}
							<a className="contact-link" href="mailto:connect@eurycele.com">
								connect@eurycele.com
							</a>
							<br />
							Country: India
						</address>
					</section>

					<section className="policy-section summary-section">
						<h2>Summary</h2>
						<p>
							Drop Shot 3D does not require account creation and does not share data with other users. The Game
							may use third-party advertising and mediation services that process limited data categories such as
							approximate location, ad-related app activity, diagnostics, and device or other identifiers for
							advertising, analytics, and fraud prevention.
						</p>
					</section>
				</div>
			</div>
		</div>
	);
};

export default DropshotPrivacyPolicy;
